import { useState, useMemo, useEffect, useCallback } from "react";
import { InboxIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import type { StylesConfig } from "react-select";
import { Select } from "shared/components";
import type { BaseOption } from "shared/components";
import { useMailboxes } from "../../settings-communication/api/hooks";
import { useCrmContact } from "../api/crm";
import { useSendOptions } from "../api/hooks";
import { usePropertyDefaultEmail } from "../../properties-id/lib/propertyDefaultEmail";
import { nylasApi, AVAILABLE_MAIL_GRANTS_QUERY_KEY } from "api/nylas";
import type { Mailbox } from "../../settings-communication/api/types";
import { getEmailIcon } from "../utils/emailProviderIcon";
import { RoutingFieldsRow } from "./RoutingFieldsRow";
import { useComposerNarrow } from "../context/useComposerNarrow";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { cx } from "shared/utils/classnames";
import {
  CompactRoutingField,
  CompactRoutingEmpty,
  CompactRoutingRow
} from "./CompactRoutingField";
import {
  FOOTER_PILL_STYLES,
  footerFromPillProps,
  footerPillClassName,
  withWarningBorder
} from "./participantFieldStyles";
import { ToRecipientsSelect } from "./ToRecipientsSelect";
import { TwoLineOptionLabel } from "./TwoLineOptionLabel";
import type { MultiRecipientControls } from "./multi-recipient";
import {
  useContactSingleProperty,
  useRecommendedEmailSender,
  useSenderMismatch,
  type SenderRecommendation
} from "../hooks/useRecommendedSender";
import {
  RecommendationReason,
  RecommendedTitle
} from "./recommendedSenderOption";
import { senderMenuWidth, type SenderOption } from "../utils/senderOption";
import { SenderMismatchDialog } from "./SenderMismatchDialog";

interface EmailRoutingSelectProps {
  contactId?: string;
  conversationId?: string;
  onSelectionChange?: (mailboxId: string, toEmail: string) => void;
  menuPlacement?: "top" | "bottom" | "auto";
  /**
   * Pop-up participants block (CRM-1133 design 2152:16771): labeled boxed
   * fields — stacked on the floating card, side by side in the drawer —
   * instead of the inline "From: … → To: …" row.
   */
  compact?: boolean;
  multiRecipient?: MultiRecipientControls;
  recommendSender?: boolean;
}

const DEFAULT_EMAIL = import.meta.env.VITE_APP_DEFAULT_EMAIL;

const NO_CONTACT_EMAIL_MESSAGE = "This contact does not have an email address";

const MailboxIcon = ({
  email,
  provider
}: {
  email: string;
  provider?: string;
}) => {
  const icon = getEmailIcon(email, provider);

  return icon ? (
    <img src={icon} alt="" className="w-4 h-4 flex-shrink-0" />
  ) : (
    <HugeiconsIcon
      icon={InboxIcon}
      size={16}
      className="text-content-300 flex-shrink-0"
    />
  );
};

const createEmailOption = ({
  email,
  subtitle,
  provider,
  recommendation
}: {
  email: string;
  subtitle?: string;
  provider?: string;
  recommendation?: SenderRecommendation;
}) => (
  <div
    className={cx(
      "flex gap-2 min-h-8",
      recommendation ? "items-start" : "items-center"
    )}
  >
    <MailboxIcon email={email} provider={provider} />
    {recommendation ? (
      <div className="min-w-0 space-y-1">
        <TwoLineOptionLabel
          title={<RecommendedTitle title={email} />}
          subtitle={subtitle}
        />
        <RecommendationReason reason={recommendation.reason} />
      </div>
    ) : (
      <TwoLineOptionLabel title={email} subtitle={subtitle} />
    )}
  </div>
);

const senderOption = ({
  value,
  email,
  provider,
  recommendation
}: {
  value: string;
  email: string;
  provider?: string;
  recommendation: SenderRecommendation | null;
}): SenderOption => ({
  value,
  label: createEmailOption({ email, provider }),
  menuLabel:
    recommendation?.value === value
      ? createEmailOption({ email, provider, recommendation })
      : undefined
});

const createGroupLabel = (label: string): BaseOption => ({
  value: `__group_${label}`,
  label: (
    <div className="px-2 h-6 flex items-center text-sm text-content-400 tracking-wide cursor-default">
      {label}
    </div>
  ),
  isDisabled: true
});

const useAvailableMailGrants = () =>
  useQuery({
    queryKey: [...AVAILABLE_MAIL_GRANTS_QUERY_KEY],
    queryFn: () => nylasApi.listAvailableMailGrants()
  });

export const EmailRoutingSelect = ({
  contactId,
  conversationId,
  onSelectionChange,
  menuPlacement = "top",
  compact = false,
  multiRecipient,
  recommendSender = false
}: EmailRoutingSelectProps) => {
  const embed = useInboxEmbed();
  const isNarrow = useComposerNarrow();
  const [selectedMailbox, setSelectedMailbox] = useState<string | null>(null);
  const [selectedToEmail, setSelectedToEmail] = useState<string | null>(null);

  const { data: contact } = useCrmContact(contactId ?? "");
  const { data: sendOptions } = useSendOptions(conversationId);

  // null → no restriction (unrestricted user, or options not loaded yet).
  const allowedToEmails = useMemo(() => {
    if (!sendOptions || sendOptions.unrestricted) return null;
    return new Set(sendOptions.emails.map((email) => email.toLowerCase()));
  }, [sendOptions]);
  const { data: mailboxes } = useMailboxes();
  const { data: availableGrants = [] } = useAvailableMailGrants();

  const singlePropertyId = useContactSingleProperty(contact)?.id;

  const { data: propertyEmailDefault } = usePropertyDefaultEmail(
    singlePropertyId ?? 0,
    singlePropertyId !== undefined
  );
  const propertyDefaultMailboxId = propertyEmailDefault?.default_mailbox_id;

  // Build a lookup: email → account_type from access-controlled grant list
  const grantByEmail = useMemo(
    () =>
      availableGrants.reduce((map, grant) => {
        map.set(grant.email, grant.account_type);
        return map;
      }, new Map<string, "personal" | "shared">()),
    [availableGrants]
  );

  const { personalMbs, sharedMbs, showDefaultAsPersonal, mailboxIdByEmail } =
    useMemo(() => {
      const active = (mailboxes ?? []).filter(
        (mb: Mailbox) =>
          mb.status === "active" && grantByEmail.has(mb.email_address)
      );

      // CRM-1165: there is no account-wide default mailbox any more, so no option
      // is marked "Default" here and nothing is pre-selected on that basis. The
      // pre-selection falls through to the first available option below (the Boom
      // system address when it is shown), and the send path still resolves the
      // property's own default whenever no mailbox is chosen explicitly.
      const personal = active.filter(
        (mb) => grantByEmail.get(mb.email_address) === "personal"
      );
      const shared = active.filter(
        (mb) => grantByEmail.get(mb.email_address) === "shared"
      );

      const showDefault =
        Boolean(DEFAULT_EMAIL) &&
        !personal.some((mb) => mb.email_address === DEFAULT_EMAIL) &&
        !shared.some((mb) => mb.email_address === DEFAULT_EMAIL);

      const idByEmail = new Map<string, string>();
      if (showDefault) idByEmail.set(DEFAULT_EMAIL.toLowerCase(), "default");
      [...personal, ...shared].forEach((mb) =>
        idByEmail.set(mb.email_address.toLowerCase(), mb.id)
      );

      return {
        personalMbs: personal,
        sharedMbs: shared,
        showDefaultAsPersonal: showDefault,
        mailboxIdByEmail: idByEmail
      };
    }, [mailboxes, grantByEmail]);

  const recommendation = useRecommendedEmailSender({
    enabled: recommendSender,
    conversationId,
    contact,
    mailboxIdByEmail
  });

  const { mailboxOptions, defaultMailboxValue } = useMemo(() => {
    const options: SenderOption[] = [];

    if (personalMbs.length > 0 || showDefaultAsPersonal) {
      options.push(createGroupLabel("Personal emails"));

      if (showDefaultAsPersonal)
        options.push(
          senderOption({
            value: "default",
            email: DEFAULT_EMAIL,
            recommendation
          })
        );

      personalMbs.forEach((mb) => {
        options.push(
          senderOption({
            value: mb.id,
            email: mb.email_address,
            provider: mb.provider,
            recommendation
          })
        );
      });
    }

    if (sharedMbs.length > 0) {
      options.push(createGroupLabel("Shared emails"));
      sharedMbs.forEach((mb) => {
        options.push(
          senderOption({
            value: mb.id,
            email: mb.email_address,
            provider: mb.provider,
            recommendation
          })
        );
      });
    }

    const defaultValue =
      options.find((o) => !o.isDisabled)?.value?.toString() ?? "";

    return { mailboxOptions: options, defaultMailboxValue: defaultValue };
  }, [personalMbs, sharedMbs, showDefaultAsPersonal, recommendation]);

  const toEmailOptions = useMemo((): BaseOption[] => {
    if (!contact) return [];
    const contactName = [contact.first_name, contact.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    const visibleEmails = allowedToEmails
      ? contact.emails.filter((email) =>
          allowedToEmails.has(email.toLowerCase())
        )
      : contact.emails;

    return visibleEmails.map(
      (email): BaseOption => ({
        value: email,
        label: createEmailOption({ email, subtitle: contactName })
      })
    );
  }, [contact, allowedToEmails]);

  const effectiveMailbox = useMemo(() => {
    if (
      selectedMailbox &&
      mailboxOptions.some((opt) => opt.value === selectedMailbox)
    )
      return selectedMailbox;
    if (recommendation) return recommendation.value;
    if (
      propertyDefaultMailboxId &&
      mailboxOptions.some((opt) => opt.value === propertyDefaultMailboxId)
    )
      return propertyDefaultMailboxId;
    return defaultMailboxValue;
  }, [
    selectedMailbox,
    mailboxOptions,
    defaultMailboxValue,
    propertyDefaultMailboxId,
    recommendation
  ]);

  const effectiveMailboxEmail = useMemo(() => {
    if (effectiveMailbox === "default") return DEFAULT_EMAIL ?? "";
    return (
      (mailboxes ?? []).find((mb: Mailbox) => mb.id === effectiveMailbox)
        ?.email_address ?? ""
    );
  }, [effectiveMailbox, mailboxes]);

  const effectiveMailboxIcon = useMemo(() => {
    if (effectiveMailbox === "default")
      return <MailboxIcon email={DEFAULT_EMAIL} />;
    const mailbox = mailboxes?.find((mb) => mb.id === effectiveMailbox);
    return mailbox ? (
      <MailboxIcon email={mailbox.email_address} provider={mailbox.provider} />
    ) : null;
  }, [effectiveMailbox, mailboxes]);

  const senderMismatch = useSenderMismatch({
    selected: selectedMailbox,
    effective: effectiveMailbox,
    recommendation
  });

  const emailByValue = useMemo(() => {
    const map = new Map<string, string>();
    mailboxIdByEmail.forEach((id, email) => map.set(id, email));
    return map;
  }, [mailboxIdByEmail]);

  const mismatchDialog = recommendation ? (
    <SenderMismatchDialog
      opened={senderMismatch.dialogOpen}
      channel="email"
      recommendation={recommendation}
      currentLabel={emailByValue.get(effectiveMailbox)}
      recommendedLabel={emailByValue.get(recommendation.value)}
      onKeep={senderMismatch.dismiss}
      onSwitch={() => setSelectedMailbox(recommendation.value)}
      zIndex={embed?.modalZIndex}
    />
  ) : null;

  const fromStyles = (styles: StylesConfig) =>
    senderMismatch.mismatched ? withWarningBorder(styles) : styles;

  const fromPill = footerFromPillProps({
    isNarrow,
    valueIcon: effectiveMailboxIcon
  });

  const effectiveToEmail = useMemo(() => {
    if (
      selectedToEmail &&
      toEmailOptions.some((opt) => opt.value === selectedToEmail)
    )
      return selectedToEmail;
    const firstValue = toEmailOptions[0]?.value;
    return typeof firstValue === "string" ? firstValue : "";
  }, [selectedToEmail, toEmailOptions]);

  const handleSelectionChange = useCallback(() => {
    if (effectiveMailbox && effectiveToEmail && onSelectionChange)
      onSelectionChange(effectiveMailbox, effectiveToEmail);
  }, [effectiveMailbox, effectiveToEmail, onSelectionChange]);

  useEffect(() => {
    handleSelectionChange();
  }, [handleSelectionChange]);

  const selectableOptions = mailboxOptions.filter((o) => !o.isDisabled);
  const hasNoToEmails = !!contact && contact.emails.length === 0;
  if (selectableOptions.length === 0) return null;
  if (!hasNoToEmails && toEmailOptions.length === 0) return null;

  if (compact)
    return (
      <CompactRoutingRow>
        <CompactRoutingField
          id="from-mailbox"
          label="From"
          value={effectiveMailbox}
          valueText={effectiveMailboxEmail}
          options={mailboxOptions}
          onChange={setSelectedMailbox}
          menuPlacement={menuPlacement}
          warning={senderMismatch.mismatched}
          placeholder="Select..."
        />
        {hasNoToEmails ? (
          <CompactRoutingEmpty label="To" message={NO_CONTACT_EMAIL_MESSAGE} />
        ) : (
          <CompactRoutingField
            id="to-email"
            label="To"
            value={effectiveToEmail}
            valueText={effectiveToEmail}
            options={toEmailOptions}
            onChange={setSelectedToEmail}
            menuPlacement={menuPlacement}
            placeholder="Select..."
          />
        )}
        {mismatchDialog}
      </CompactRoutingRow>
    );

  let toField = (
    <Select
      id="to-email"
      hideOptional
      searchable={false}
      value={effectiveToEmail}
      onChange={(e) => setSelectedToEmail(e.target.value)}
      options={toEmailOptions}
      className="!border-0 !shadow-none !bg-transparent !text-content-1000 !font-medium !p-0"
      legacyErrorMargins={false}
      menuPlacement={menuPlacement}
      placeholder="Select..."
    />
  );
  if (hasNoToEmails)
    toField = (
      <Select
        id="to-email"
        hideOptional
        searchable={false}
        value=""
        onChange={() => {}}
        options={[]}
        className={footerPillClassName({ isNarrow })}
        styles={FOOTER_PILL_STYLES}
        legacyErrorMargins={false}
        isDisabled
        menuPlacement={menuPlacement}
        placeholder={NO_CONTACT_EMAIL_MESSAGE}
      />
    );
  else if (multiRecipient && contactId)
    toField = (
      <ToRecipientsSelect
        channel="email"
        contactId={Number(contactId)}
        contactName={
          [contact?.first_name, contact?.last_name]
            .filter(Boolean)
            .join(" ")
            .trim() || ""
        }
        addresses={toEmailOptions
          .filter((option) => typeof option.value === "string")
          .map((option) => ({ value: option.value as string }))}
        selectedAddress={effectiveToEmail}
        onSelectAddress={setSelectedToEmail}
        extraRecipients={multiRecipient.extraRecipients}
        onAddRecipient={multiRecipient.onAddRecipient}
        onRemoveRecipient={multiRecipient.onRemoveRecipient}
        primaryExcluded={multiRecipient.primaryExcluded}
        onPrimaryExcludedChange={multiRecipient.onPrimaryExcludedChange}
      />
    );

  return (
    <>
      <RoutingFieldsRow
        from={
          <Select
            id="from-mailbox"
            hideOptional
            searchable={false}
            value={effectiveMailbox}
            onChange={(e) => setSelectedMailbox(e.target.value)}
            options={mailboxOptions}
            {...fromPill}
            styles={fromStyles(fromPill.styles)}
            menuWidth={senderMenuWidth(recommendation)}
            legacyErrorMargins={false}
            isDisabled={selectableOptions.length === 0}
            menuPlacement={menuPlacement}
            placeholder={
              selectableOptions.length === 0
                ? "No mailboxes available"
                : "Select..."
            }
          />
        }
        to={toField}
      />
      {mismatchDialog}
    </>
  );
};
